import { X } from "lucide-react";
import { useEffect, useState } from "react";
import yaml from 'js-yaml';
import '../../../assets/styles/GuardrailsManager.css';
import { buildApiUrl } from "../../../services/apiConfig";

const API_HOST = '18.208.58.92';

const GUARDRAILS_OPTIONS = [
    "Input Check",
    "Output Check",
    "Hallucination Check"
];

const types = ['self_check_input', 'self_check_output', 'self_check_hallucination'];

export default function GuardrailsDetailModal({ selectedUseCaseId, closeModal }) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [selectedGuardrailsOption, setSelectedGuardrailsOption] = useState(GUARDRAILS_OPTIONS[0]);
    const [promptTemplates, setPromptTemplates] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const handleOption = (index, option) => {
        setSelectedGuardrailsOption(option);
        setSelectedIndex(index);
    };

    const fetchPromptTemplates = async (configName) => {
        setIsLoading(true);
        try {
            const templates = {};

            for (const type of types) {
                const response = await fetch(buildApiUrl('GUARD', `/guardrails/config/${configName}/prompt/${type}`));
                if (response.ok) {
                    const data = await response.json();
                    const content = data.content;

                    let parsed;
                    try {
                        if (typeof content === 'string' && (content.trim().startsWith('{') || content.trim().startsWith('['))) {
                            parsed = JSON.parse(content);
                        } else {
                            parsed = content;
                        }

                        // Convert to YAML and then clean it for display
                        const yamlText = yaml.dump(parsed);
                        templates[type] = formatYamlForDisplay(yamlText);
                    } catch (err) {
                        console.warn(`Could not convert ${type} to YAML. Using raw content.`, err);
                        templates[type] = content;
                    }
                } else {
                    templates[type] = ''; // Default empty if not found
                }
            }

            setPromptTemplates(templates);
        } catch (error) {
            console.error('Error fetching prompt templates:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Format YAML to plain readable text
    const formatYamlForDisplay = (yamlText) => {
        return yamlText
            .replace(/^- /gm, '• ') // bullets
            .replace(/"/g, '')      // remove quotes
            .replace(/:\s*/g, ': ') // normalize spacing
            .trim();
    };

    useEffect(() => {
        fetchPromptTemplates("dashboard");
    }, []);

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    closeModal();
                }
            }}
        >
            <div
                className="bg-white rounded-lg p-4 overflow-y-auto shadow-xl transform transition-all animate-modalFade"
                style={{
                    width: '550px',
                    maxWidth: '90vw',
                    maxHeight: '80vh',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2)'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-3 sticky top-0 bg-white pb-2 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-gray-800">Guardrail Details</h2>
                    <button
                        onClick={closeModal}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded-full"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                        <p className="text-sm font-medium text-primary mb-2">Guardrail Type</p>
                        <div className="flex flex-wrap gap-2">
                            {GUARDRAILS_OPTIONS.map((option, index) => (
                                <button
                                    key={option}
                                    className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                                        selectedGuardrailsOption === option 
                                            ? "bg-primary text-white" 
                                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                    }`}
                                    onClick={() => handleOption(index, option)}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <p className="text-sm font-medium text-primary mb-2">Guardrail Configuration</p>
                        {isLoading ? (
                            <div className="bg-gray-50 p-4 rounded border border-gray-200 h-64 flex items-center justify-center">
                                <p className="text-gray-500">Loading configuration...</p>
                            </div>
                        ) : (
                            <div className="bg-gray-50 p-3 rounded border border-gray-200 max-h-96 overflow-y-auto font-mono text-sm whitespace-pre-wrap">
                                {promptTemplates[types[selectedIndex]] || 'No configuration available'}
                            </div>
                        )}
                    </div>

                    <div className="mt-4 pt-2 border-t border-gray-100 flex justify-end">
                        <button
                            className="bg-primary text-white px-4 py-1.5 rounded text-sm hover:bg-primaryDark"
                            onClick={closeModal}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}